import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { STATUS_META, FUNNEL_STAGES } from "../lib/leadStatus";
import {
  MessageSquare, Bot, Send, MessagesSquare, UserPlus, Flame, ArrowUpRight, CalendarDays, Clock,
} from "lucide-react";

function FunnelCard({ stats, navigate }) {
  const [fp, setFp] = useState("week");
  const funnel = stats?.funnel?.[fp] || {};
  const conv = stats?.conversion?.[fp] ?? 0;
  const top = funnel.new || 0;
  const stageColors = {
    new: "bg-slate-400", contacted: "bg-blue-500", qualified: "bg-indigo-500",
    quoted: "bg-amber-500", advance_paid: "bg-teal-500", won: "bg-emerald-500",
  };
  return (
    <div className="border border-[#E9EDEF] bg-white rounded-lg p-5" data-testid="dashboard-funnel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-heading text-sm font-medium text-[#111B21]">Outcome Funnel</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#54656F]" data-testid="funnel-conversion">
            Conversion: <span className="font-semibold text-[#075E54]">{conv}%</span>
          </span>
          <div className="flex gap-1 border border-[#E9EDEF] rounded-lg p-0.5 bg-[#F0F2F5]">
            {["week", "month", "total"].map((p) => (
              <button key={p} data-testid={`funnel-period-${p}`} onClick={() => setFp(p)}
                className={`text-[11px] px-2.5 py-1 rounded-md capitalize transition-colors ${fp === p ? "bg-white text-[#111B21] font-semibold shadow-sm" : "text-[#8696A0]"}`}>
                {p === "total" ? "All time" : `This ${p}`}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {FUNNEL_STAGES.map((s) => {
          const n = funnel[s] || 0;
          const pct = top ? Math.round((n / top) * 100) : 0;
          return (
            <button key={s} data-testid={`funnel-stage-${s}`}
              onClick={() => navigate(`/leads`)}
              className="w-full flex items-center gap-3 group">
              <span className="w-24 text-right text-xs text-[#54656F] shrink-0">{STATUS_META[s].label}</span>
              <div className="flex-1 h-6 bg-[#F0F2F5] rounded-md overflow-hidden">
                <div className={`h-full ${stageColors[s]} transition-all group-hover:opacity-90`} style={{ width: `${Math.max(pct, n ? 6 : 0)}%` }} />
              </div>
              <span className="w-16 text-xs font-semibold text-[#111B21] shrink-0">{n} <span className="text-[#8696A0] font-normal">· {pct}%</span></span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function leadBadge(cat) {
  const map = {
    "Very Hot": "bg-red-500/10 text-red-600 border-red-500/20",
    Hot: "bg-red-500/10 text-red-600 border-red-500/20",
    Warm: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    Cold: "bg-[#F0F2F5] text-[#54656F] border-[#E9EDEF]",
  };
  return map[cat] || map.Cold;
}

function rankWeight(cat) {
  if (cat === "Very Hot" || cat === "Hot") return 3;
  if (cat === "Warm") return 2;
  return 1;
}
function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d;
}
function isToday(v) {
  const d = parseDate(v);
  if (!d) return false;
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}
function fmtDate(v) {
  const d = parseDate(v);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function StatCard({ icon: Icon, label, value, dot, dotColor, testid }) {
  return (
    <div data-testid={testid} className="relative border border-[#E9EDEF] bg-white rounded-lg p-5">
      {dot && <span className={`absolute top-4 right-4 h-2 w-2 rounded-full ${dotColor}`} />}
      <Icon className="h-4 w-4 text-[#8696A0] mb-6" />
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">{label}</p>
      <p className="font-heading text-3xl font-light text-[#111B21] mt-1">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [allLeads, setAllLeads] = useState([]);
  const [leadScope, setLeadScope] = useState("today");
  const [leadSort, setLeadSort] = useState("date");
  const [clock, setClock] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const fetchStats = (params = {}) =>
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    fetchStats();
    api.get("/leads").then((r) => setAllLeads(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (period === "custom" && customFrom && customTo) {
      fetchStats({ start: customFrom, end: customTo });
    }
  }, [period, customFrom, customTo]);

  const waConnected = stats?.whatsapp_status === "LIVE";
  const aiActive = stats?.ai_status === "ACTIVE";
  const ranks = stats?.lead_ranks_by_period?.[period] || stats?.lead_ranks || {};
  const periodLabel = { today: "Today", week: "This Week", month: "This Month", total: "All Time", custom: "Custom Range" }[period];
  const todayStr = new Date().toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

  const displayLeads = (() => {
    let list = leadScope === "today" ? allLeads.filter((l) => isToday(l.created_at)) : [...allLeads];
    if (leadSort === "date") {
      list.sort((a, b) => (parseDate(b.created_at) || 0) - (parseDate(a.created_at) || 0));
    } else {
      const target = leadSort;
      list.sort((a, b) => {
        // rank the chosen bucket first, then by rank weight desc, then date desc
        const aw = a.category === target || (target === "Hot" && a.category === "Very Hot") ? 1 : 0;
        const bw = b.category === target || (target === "Hot" && b.category === "Very Hot") ? 1 : 0;
        if (aw !== bw) return bw - aw;
        const rw = rankWeight(b.category) - rankWeight(a.category);
        if (rw !== 0) return rw;
        return (parseDate(b.created_at) || 0) - (parseDate(a.created_at) || 0);
      });
    }
    return list.slice(0, 8);
  })();

  return (
    <>
      <PageHeader eyebrow="MuviArts Workspace" title="Dashboard"
        actions={<span className="flex items-center gap-1.5 text-xs font-mono text-[#075E54] bg-[#E7FCE8] rounded-full px-3 py-1.5" data-testid="dashboard-clock"><Clock className="h-3.5 w-3.5" /> {clock} IST</span>} />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 space-y-6">
        {/* Status row */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard testid="stat-whatsapp" icon={MessageSquare} label="WhatsApp"
            value={<span className="text-base">{waConnected ? "Connected" : (stats?.whatsapp_configured ? "Action Req." : "Not Connected")}</span>}
            dot dotColor={waConnected ? "bg-green-500" : stats?.whatsapp_configured ? "bg-amber-500" : "bg-[#CBD5E1]"} />
          <StatCard testid="stat-ai" icon={Bot} label="AI Agent"
            value={<span className="text-base">{aiActive ? "Active" : "Inactive"}</span>}
            dot dotColor={aiActive ? "bg-[#25D366]" : "bg-[#CBD5E1]"} />
          <StatCard testid="stat-messages" icon={Send} label="Messages Today" value={stats?.messages_today ?? 0} />
          <StatCard testid="stat-conversations" icon={MessagesSquare} label="Active Convos" value={stats?.active_conversations ?? 0} />
          <StatCard testid="stat-new-leads" icon={UserPlus} label="New Leads" value={stats?.new_leads ?? 0} />
          <StatCard testid="stat-hot-leads" icon={Flame} label="Hot Leads" value={stats?.hot_leads ?? 0} />
        </div>

        {/* Leads overview: every chat counts as a lead — by day / week / month / total / custom */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-5" data-testid="leads-overview">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading text-sm font-medium text-[#111B21]">Leads Overview</h3>
              <p className="text-[11px] text-[#8696A0] mt-0.5">Every WhatsApp chat is counted as a lead · <span className="text-[#075E54] font-medium" data-testid="dashboard-today-date">{todayStr}</span></p>
            </div>
            <div className="flex gap-1 bg-[#F0F2F5] rounded-lg p-1 flex-wrap">
              {[["today", "Day"], ["week", "Week"], ["month", "Month"], ["total", "Total"], ["custom", "Custom"]].map(([k, l]) => (
                <button key={k} data-testid={`leads-period-${k}`} onClick={() => setPeriod(k)}
                  className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${period === k ? "bg-white text-[#075E54] shadow-sm" : "text-[#8696A0] hover:text-[#54656F]"}`}>{l}</button>
              ))}
            </div>
          </div>

          {period === "custom" && (
            <div className="flex items-center gap-2 mt-4 flex-wrap" data-testid="leads-custom-range">
              <label className="text-xs text-[#54656F]">From</label>
              <input type="date" value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)}
                data-testid="leads-custom-from"
                className="text-xs border border-[#E9EDEF] rounded-md px-2.5 py-1.5 text-[#111B21] focus:outline-none focus:border-[#25D366]" />
              <label className="text-xs text-[#54656F]">To</label>
              <input type="date" value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)}
                data-testid="leads-custom-to"
                className="text-xs border border-[#E9EDEF] rounded-md px-2.5 py-1.5 text-[#111B21] focus:outline-none focus:border-[#25D366]" />
              {period === "custom" && (!customFrom || !customTo) && (
                <span className="text-[11px] text-[#8696A0]">Pick a start and end date</span>
              )}
            </div>
          )}

          <div className="flex items-end gap-6 mt-4 flex-wrap">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">{periodLabel} Leads</p>
              <p className="font-heading text-4xl font-light text-[#111B21] mt-1" data-testid="leads-period-value">{stats?.lead_periods?.[period] ?? 0}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[["Hot", "🔥 Hot", "bg-red-500/10 text-red-600 border-red-500/20"], ["Warm", "Warm", "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"], ["Cold", "Cold", "bg-[#F0F2F5] text-[#54656F] border-[#E9EDEF]"]].map(([k, l, cls]) => (
                <div key={k} data-testid={`leads-rank-${k}`} className={`px-3 py-2 rounded-lg border ${cls}`}>
                  <p className="text-[10px] font-mono uppercase tracking-wider opacity-80">{l}</p>
                  <p className="text-xl font-light mt-0.5">{ranks?.[k] ?? 0}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <FunnelCard stats={stats} navigate={navigate} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Recent conversations */}
          <div className="lg:col-span-2 border border-[#E9EDEF] bg-white rounded-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E9EDEF]">
              <h3 className="font-heading text-sm font-medium text-[#111B21]">Recent Conversations</h3>
              <button onClick={() => navigate("/inbox")} data-testid="dashboard-open-inbox"
                className="text-xs text-[#075E54] hover:text-[#128C7E] flex items-center gap-1 transition-colors">
                Open inbox <ArrowUpRight className="h-3 w-3" />
              </button>
            </div>
            <div className="divide-y divide-[#E9EDEF]">
              {(stats?.recent_conversations || []).length === 0 && (
                <p className="px-5 py-8 text-sm text-[#8696A0] text-center">No conversations yet. They appear when customers message your WhatsApp number.</p>
              )}
              {(stats?.recent_conversations || []).map((c) => (
                <div key={c.conversation_id} onClick={() => navigate("/inbox")}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[#F0F2F5] cursor-pointer transition-colors">
                  <div className="h-8 w-8 rounded-lg bg-[#F0F2F5] flex items-center justify-center text-xs text-[#374151]">
                    {(c.contact_name || "?")[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#111B21] truncate">{c.contact_name}</p>
                    <p className="text-xs text-[#8696A0] truncate">{c.last_message || "—"}</p>
                  </div>
                  <span className={`font-mono text-[10px] px-2 py-0.5 rounded-lg border ${c.ai_mode === "AI_ACTIVE" ? "bg-[#E7FCE8] text-[#075E54] border-[#25D366]/30" : "bg-[#E7FCE8] text-[#075E54] border-[#25D366]/30"}`}>
                    {c.ai_mode === "AI_ACTIVE" ? "AI" : "HUMAN"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent leads */}
          <div className="border border-[#E9EDEF] bg-white rounded-lg" data-testid="recent-leads">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E9EDEF]">
              <h3 className="font-heading text-sm font-medium text-[#111B21]">Recent Leads</h3>
              <button onClick={() => navigate("/leads")} className="text-xs text-[#075E54] hover:text-[#128C7E] flex items-center gap-1 transition-colors">
                All leads <ArrowUpRight className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-[#E9EDEF] bg-[#F9FAFB]">
              <div className="flex gap-1 bg-white border border-[#E9EDEF] rounded-md p-0.5">
                {[["today", "Today"], ["all", "All"]].map(([k, l]) => (
                  <button key={k} data-testid={`recent-leads-scope-${k}`} onClick={() => setLeadScope(k)}
                    className={`text-[11px] px-2.5 py-1 rounded font-medium transition-colors ${leadScope === k ? "bg-[#075E54] text-white" : "text-[#8696A0] hover:text-[#54656F]"}`}>{l}</button>
                ))}
              </div>
              <select value={leadSort} onChange={(e) => setLeadSort(e.target.value)} data-testid="recent-leads-sort"
                className="text-[11px] border border-[#E9EDEF] rounded-md px-2 py-1 text-[#54656F] bg-white focus:outline-none focus:border-[#25D366]">
                <option value="date">Sort: Date</option>
                <option value="Hot">Sort: Hot first</option>
                <option value="Warm">Sort: Warm first</option>
                <option value="Cold">Sort: Cold first</option>
              </select>
            </div>
            <div className="divide-y divide-[#E9EDEF]">
              {displayLeads.length === 0 && (
                <p className="px-5 py-8 text-sm text-[#8696A0] text-center" data-testid="recent-leads-empty">
                  {leadScope === "today" ? "No leads today yet." : "No leads yet."}
                </p>
              )}
              {displayLeads.map((l) => (
                <div key={l.lead_id} className="px-5 py-3" data-testid={`recent-lead-${l.lead_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-[#111B21] truncate">{l.name || l.phone}</p>
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-lg border shrink-0 ${leadBadge(l.category)}`}>{l.category}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <CalendarDays className="h-3 w-3 text-[#8696A0]" />
                    <p className="text-xs text-[#8696A0]" data-testid={`recent-lead-date-${l.lead_id}`}>Started {fmtDate(l.created_at)}</p>
                  </div>
                  {(l.active_automations || []).length > 0 && (
                    <p className="text-[11px] font-medium text-[#075E54] mt-1 truncate" data-testid={`recent-lead-automations-${l.lead_id}`}>
                      {l.active_automations.map((a) => a.name).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
